import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Search, ArrowLeft, Container as ContainerIcon, Truck, X as XIcon, Wrench, Plus, Edit2, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/Layout";
import { GateBadge, StatusBadge } from "../components/StatusBadge";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import GateOutDialog from "../components/GateOutDialog";
import YardServiceDialog from "../components/YardServiceDialog";
import CorrectContainerNumberDialog from "../components/CorrectContainerNumberDialog";

const ISO_CATEGORIES = [
  { code: "STD", label: "STD",  tone: "bg-gray-100 text-gray-800 border-gray-300" },
  { code: "HC",  label: "HC",   tone: "bg-sky-100 text-sky-800 border-sky-300" },
  { code: "TNK", label: "TNK",  tone: "bg-[#F1471D]/15 text-[#9b2a0f] border-[#F1471D]/50" },
  { code: "RF",  label: "RF",   tone: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { code: "OT",  label: "OT",   tone: "bg-amber-100 text-amber-900 border-amber-300" },
  { code: "FR",  label: "FR",   tone: "bg-violet-100 text-violet-800 border-violet-300" },
  { code: "FLT", label: "FLT",  tone: "bg-rose-100 text-rose-800 border-rose-300" },
];

export function Containers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("in"); // 'in' | 'out' | 'all'
  const [carrier, setCarrier] = useState("");
  const [carriers, setCarriers] = useState([]);
  const [customer, setCustomer] = useState("");
  const [customers, setCustomers] = useState([]);
  const [isoCats, setIsoCats] = useState([]); // ['STD','HC',...]
  const [length, setLength] = useState("");  // '' | '20' | '40'
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [yard, setYard] = useState(""); // '' = all yards
  const [yards, setYards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gateOut, setGateOut] = useState(null);
  const reqIdRef = useRef(0);

  const load = () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (filter === "in") params.append("in_depot", "true");
    else if (filter === "out") params.append("in_depot", "false");
    if (carrier) params.append("carrier_code", carrier);
    if (customer) params.append("customer", customer);
    if (yard) params.append("yard_code", yard);
    if (isoCats.length) params.append("iso_categories", isoCats.join(","));
    if (length) params.append("length", length);
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    params.append("limit", "200");
    api.get(`/containers?${params.toString()}`)
      .then((r) => {
        if (reqId === reqIdRef.current) setItems(r.data);
      })
      .finally(() => {
        if (reqId === reqIdRef.current) setLoading(false);
      });
  };

  useEffect(() => {
    const p = new URLSearchParams();
    if (filter === "in") p.append("in_depot", "true");
    else if (filter === "out") p.append("in_depot", "false");
    api.get(`/containers/carriers?${p.toString()}`).then((r) => setCarriers(r.data)).catch(() => {});
  }, [filter]);

  useEffect(() => {
    api.get("/containers/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
    api.get("/yards").then((r) => setYards(r.data || [])).catch(() => {});
  }, []);

  // Re-fetch yard counts whenever the in-depot state may have changed.
  const refreshYards = () =>
    api.get("/yards").then((r) => setYards(r.data || [])).catch(() => {});

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, filter, carrier, customer, yard, isoCats, length, dateFrom, dateTo]);

  const toggleIsoCat = (code) =>
    setIsoCats((arr) => arr.includes(code) ? arr.filter((x) => x !== code) : [...arr, code]);

  const activeCount = useMemo(() => {
    let n = 0;
    if (q) n++;
    if (carrier) n++;
    if (customer) n++;
    if (yard) n++;
    if (isoCats.length) n++;
    if (length) n++;
    if (dateFrom || dateTo) n++;
    return n;
  }, [q, carrier, customer, yard, isoCats, length, dateFrom, dateTo]);

  const clearAll = () => {
    setQ(""); setCarrier(""); setCustomer(""); setYard("");
    setIsoCats([]); setLength(""); setDateFrom(""); setDateTo("");
  };

  const filterBtn = (v, label) => (
    <button
      onClick={() => setFilter(v)}
      data-testid={`filter-containers-${v}`}
      className={`ps-btn h-8 px-3 text-xs ${filter === v ? "ps-btn-primary" : "ps-btn-secondary"}`}
    >
      {label}
    </button>
  );

  return (
    <div data-testid="containers-page">
      <PageHeader
        title="Containers in the yard"
        subtitle="Master record — one row per container. Shows each unit's current location and last gate status."
        testid="containers-header"
      />
      <div className="p-6 sm:p-8 space-y-4">
        {/* Yard pills — switch between physical yards and show live in-depot counts. */}
        {yards.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="yard-pills">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mr-1">Yard</span>
            <button
              type="button"
              onClick={() => setYard("")}
              data-testid="yard-pill-ALL"
              className={`text-xs font-bold tracking-wide px-3 py-1.5 rounded-sm border transition-colors ${
                yard === "" ? "bg-[#006E9E] text-white border-[#006E9E]" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            >
              All <span className="font-mono ml-1 opacity-80">{yards.reduce((s, y) => s + (y.in_depot || 0), 0)}</span>
            </button>
            {yards.map((y) => (
              <button
                key={y.code}
                type="button"
                onClick={() => setYard(y.code)}
                data-testid={`yard-pill-${y.code}`}
                className={`text-xs font-bold tracking-wide px-3 py-1.5 rounded-sm border transition-colors ${
                  yard === y.code ? "bg-[#006E9E] text-white border-[#006E9E]" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {y.name} <span className="font-mono ml-1 opacity-80">{y.in_depot}</span>
              </button>
            ))}
          </div>
        )}
        <div className="border border-gray-200 bg-white p-4 space-y-3">
          {/* Row 1: search + status + carrier + customer */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative max-w-sm flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value.toUpperCase())}
                placeholder="Container number"
                className="ps-input font-mono pl-9"
                data-testid="containers-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Show</span>
              {filterBtn("in", "In depot")}
              {filterBtn("out", "Gated out")}
              {filterBtn("all", "All")}
            </div>
            <select
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              className="ps-input font-mono max-w-[180px]"
              data-testid="containers-carrier-filter"
            >
              <option value="">All carriers</option>
              {carriers.map((c) => (
                <option key={c.carrier} value={c.carrier}>{c.carrier} ({c.count})</option>
              ))}
            </select>
            <div className="relative">
              <input
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                placeholder="Customer"
                list="containers-customer-list"
                className="ps-input max-w-[220px]"
                data-testid="containers-customer-filter"
              />
              <datalist id="containers-customer-list">
                {customers.slice(0, 80).map((c) => (
                  <option key={c.customer} value={c.customer}>{c.count}</option>
                ))}
              </datalist>
            </div>
          </div>

          {/* Row 2: type chips + length + date range */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1 border-t border-gray-100">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mr-1">Type</span>
              {ISO_CATEGORIES.map((c) => {
                const on = isoCats.includes(c.code);
                return (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => toggleIsoCat(c.code)}
                    data-testid={`iso-chip-${c.code}`}
                    className={`text-[11px] font-bold tracking-wide px-2.5 py-1 rounded-sm border transition-colors ${
                      on ? c.tone + " ring-2 ring-offset-1 ring-[#006E9E]/40" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                    }`}
                  >{c.label}</button>
                );
              })}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mr-1">Size</span>
              {["20", "40"].map((sz) => (
                <button
                  key={sz}
                  type="button"
                  onClick={() => setLength(length === sz ? "" : sz)}
                  data-testid={`length-chip-${sz}`}
                  className={`text-[11px] font-bold tracking-wide px-2.5 py-1 rounded-sm border transition-colors ${
                    length === sz ? "bg-[#006E9E] text-white border-[#006E9E]" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                  }`}
                >{sz}&prime;</button>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mr-1">Date in</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="ps-input h-8 text-xs"
                data-testid="containers-date-from"
              />
              <span className="text-gray-400">→</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="ps-input h-8 text-xs"
                data-testid="containers-date-to"
              />
            </div>
            <div className="ml-auto flex items-center gap-3 text-xs">
              <span className="font-mono tabular-nums text-gray-500" data-testid="containers-count">
                {loading ? "…" : items.length} result{items.length === 1 ? "" : "s"}
                {activeCount > 0 && <> · <span className="text-[#006E9E] font-semibold">{activeCount} filter{activeCount === 1 ? "" : "s"}</span></>}
              </span>
              {activeCount > 0 && (
                <button
                  onClick={clearAll}
                  className="inline-flex items-center gap-1 text-[#F1471D] font-bold hover:underline"
                  data-testid="containers-clear-filters"
                >
                  <XIcon className="w-3 h-3" /> Clear all
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="ps-table ps-table-cards" data-testid="containers-table">
            <thead>
              <tr>
                <th>Container</th>
                <th>Carrier</th>
                <th>ISO type</th>
                <th>Yard</th>
                <th>Last gate</th>
                <th>Last event</th>
                <th>Last location</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-10 text-gray-400">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-gray-400">No containers found.</td></tr>}
              {!loading && items.map((c) => (
                <tr key={c.container_number} data-testid={`container-row-${c.container_number}`}>
                  <td data-label="Container" className="font-mono font-semibold">{c.container_number}</td>
                  <td data-label="Carrier" className="font-mono font-bold">{c.carrier || <span className="text-gray-400">—</span>}</td>
                  <td data-label="ISO type" className="font-mono">{c.iso_type}</td>
                  <td data-label="Yard">
                    <span
                      className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-gray-300 bg-gray-50 text-gray-700"
                      data-testid={`container-yard-${c.container_number}`}
                    >
                      {(yards.find((y) => y.code === (c.yard_code || "TEMA"))?.name) || c.yard_code || "Tema (Main)"}
                    </span>
                  </td>
                  <td data-label="Last gate">{c.last_status ? <GateBadge type={c.last_status} /> : "—"}</td>
                  <td data-label="Last event" className="font-mono text-gray-700">{c.last_event_at ? new Date(c.last_event_at).toLocaleString() : "—"}</td>
                  <td data-label="Last location" className="font-mono">{c.last_location_code || "—"}</td>
                  <td data-label="Actions" className="text-right whitespace-nowrap mobile-row-full">
                    {c.last_status === "IN" && (
                      <button
                        onClick={() => setGateOut(c)}
                        className="ps-btn ps-btn-primary h-8 px-3 text-xs mr-1"
                        data-testid={`gate-out-${c.container_number}`}
                      >
                        <Truck className="w-3.5 h-3.5" /> Gate OUT
                      </button>
                    )}
                    <Link to={`/containers/${c.container_number}`} className="text-[#006E9E] text-xs font-semibold hover:underline" data-testid={`view-container-${c.container_number}`}>
                      History →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <GateOutDialog
        container={gateOut}
        onClose={() => setGateOut(null)}
        onDone={() => { load(); refreshYards(); }}
      />
    </div>
  );
}

export function ContainerDetail() {
  const { number } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCorrectCn = ["admin", "supervisor", "general_manager", "depot_manager"].includes(user?.role);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [gateOut, setGateOut] = useState(null);
  const [yardSvcOpen, setYardSvcOpen] = useState(false);
  const [editingYs, setEditingYs] = useState(null);
  const [correctCnOpen, setCorrectCnOpen] = useState(false);

  const load = () => {
    api.get(`/containers/${number}`)
      .then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(e?.response?.status === 404 ? "Container not found" : "Failed to load"));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [number]);

  if (err) {
    return (
      <div className="p-10 text-center text-gray-500" data-testid="container-detail-error">
        <ContainerIcon className="w-8 h-8 mx-auto mb-3 text-gray-300" />
        {err}
        <div className="mt-4"><Link to="/containers" className="text-[#006E9E] text-sm font-semibold hover:underline">← Back to containers</Link></div>
      </div>
    );
  }
  if (!data) return <div className="p-10 text-gray-400 text-sm">Loading…</div>;

  const { container, history } = data;
  const yardServices = data.yard_services || [];
  const canGateOut = container.last_status === "IN";
  const canLogYardService = container.last_status === "IN";
  const pendingYs = yardServices.filter((y) => y.invoice_status === "PENDING");

  const deleteYardService = async (ys) => {
    if (!window.confirm(`Delete ${ys.service_label || ys.service_code}?`)) return;
    try {
      await api.delete(`/yard-services/${ys.id}`);
      toast.success("Service removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  return (
    <div data-testid="container-detail-page">
      <PageHeader
        title={container.container_number}
        subtitle={`ISO ${container.iso_type} · last location ${container.last_location_code || "—"}`}
        testid="container-detail-header"
        actions={
          <>
            {canCorrectCn && (
              <button
                onClick={() => setCorrectCnOpen(true)}
                className="ps-btn ps-btn-secondary"
                data-testid="correct-cn-button"
                title="Fix a typo in this container number"
              >
                <Pencil className="w-4 h-4" /> Correct number
              </button>
            )}
            {canLogYardService && (
              <button
                onClick={() => { setEditingYs(null); setYardSvcOpen(true); }}
                className="ps-btn ps-btn-secondary"
                data-testid="log-yard-service-button"
              >
                <Wrench className="w-4 h-4" /> Log service
              </button>
            )}
            {canGateOut && (
              <button onClick={() => setGateOut(container)} className="ps-btn ps-btn-primary" data-testid="gate-out-button">
                <Truck className="w-4 h-4" /> Gate OUT
              </button>
            )}
            <Link to="/containers" className="ps-btn ps-btn-secondary" data-testid="back-to-containers">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </>
        }
      />
      <div className="p-6 sm:p-8 space-y-6">
        <div className="border border-gray-200 bg-white" data-testid="yard-services-panel">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600 flex items-center gap-2">
              <Wrench className="w-3.5 h-3.5" /> Yard services
              {pendingYs.length > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-mono px-1.5 py-0.5 rounded-sm">
                  {pendingYs.length} pending
                </span>
              )}
            </div>
            {canLogYardService && (
              <button
                onClick={() => { setEditingYs(null); setYardSvcOpen(true); }}
                className="text-xs font-semibold text-[#006E9E] hover:underline inline-flex items-center gap-1"
                data-testid="add-yard-service-inline"
              >
                <Plus className="w-3.5 h-3.5" /> Add service
              </button>
            )}
          </div>
          {yardServices.length === 0 ? (
            <div className="text-center text-gray-400 py-6 text-sm" data-testid="yard-services-empty">
              No in-yard services logged for this container yet.
              {canLogYardService && (
                <>
                  {" "}
                  <button
                    onClick={() => { setEditingYs(null); setYardSvcOpen(true); }}
                    className="text-[#006E9E] hover:underline font-semibold"
                  >
                    Log the first one
                  </button>
                  .
                </>
              )}
            </div>
          ) : (
            <table className="ps-table" data-testid="yard-services-table">
              <thead>
                <tr>
                  <th>Performed</th>
                  <th>Service</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Unit</th>
                  <th className="text-right">Total</th>
                  <th>Notes</th>
                  <th>By</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {yardServices.map((y) => (
                  <tr key={y.id} data-testid={`yard-service-row-${y.id}`}>
                    <td className="font-mono text-xs">{new Date(y.performed_at).toLocaleString()}</td>
                    <td className="font-semibold">{y.service_label || y.service_code}</td>
                    <td className="text-right font-mono">{Number(y.qty || 0).toFixed(1)}</td>
                    <td className="text-right font-mono">${Number(y.unit_price || 0).toFixed(2)}</td>
                    <td className="text-right font-mono font-semibold">${Number(y.total || y.qty * y.unit_price || 0).toFixed(2)}</td>
                    <td className="text-xs text-gray-600">{y.notes || "—"}</td>
                    <td className="text-xs text-gray-500">{y.performed_by}</td>
                    <td>
                      <span
                        className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded-sm ${
                          y.invoice_status === "INVOICED"
                            ? "bg-green-100 text-green-800 border border-green-300"
                            : "bg-amber-100 text-amber-900 border border-amber-300"
                        }`}
                      >
                        {y.invoice_status}
                      </span>
                    </td>
                    <td className="text-right">
                      {y.invoice_status === "PENDING" && (
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => { setEditingYs(y); setYardSvcOpen(true); }}
                            className="text-gray-500 hover:text-[#006E9E]"
                            data-testid={`edit-yard-service-${y.id}`}
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteYardService(y)}
                            className="text-gray-500 hover:text-red-600"
                            data-testid={`delete-yard-service-${y.id}`}
                            title="Void"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="px-5 py-2 border-t border-gray-100 text-[11px] text-gray-500">
            Services logged here are rolled onto the carrier's monthly invoice at generation time.
          </div>
        </div>

        <div className="border border-gray-200 bg-white">
          <div className="px-5 py-3 border-b border-gray-200 text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600">
            Movement history
          </div>
          <table className="ps-table" data-testid="history-table">
            <thead>
              <tr>
                <th>Event time</th>
                <th>Gate</th>
                <th>Load</th>
                <th>Location</th>
                <th>Carrier</th>
                <th>Customer · Contact</th>
                <th>Truck · Driver</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={8} className="text-center text-gray-400 py-8">No events.</td></tr>}
              {history.map((e) => (
                <tr key={e.id} data-testid={`history-row-${e.id}`}>
                  <td className="font-mono">{new Date(e.event_time).toLocaleString()}</td>
                  <td><GateBadge type={e.gate_type} /></td>
                  <td>{e.is_full ? "Full" : "Empty"}</td>
                  <td className="font-mono">{e.location_code}</td>
                  <td className="font-mono">{e.last_carrier || e.carrier_code || "—"}</td>
                  <td>
                    <div>{e.customer || "—"}</div>
                    {e.contact_number && <div className="text-xs text-gray-500 font-mono">{e.contact_number}</div>}
                  </td>
                  <td className="text-xs">
                    {e.truck_plate ? <div className="font-mono">{e.truck_plate}</div> : null}
                    {e.driver_name ? <div className="text-gray-600">{e.driver_name}</div> : null}
                    {!e.truck_plate && !e.driver_name ? <span className="text-gray-400">—</span> : null}
                  </td>
                  <td><StatusBadge status={e.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <GateOutDialog
        container={gateOut}
        onClose={() => setGateOut(null)}
        onDone={() => load()}
      />

      {yardSvcOpen && (
        <YardServiceDialog
          container={container}
          entry={editingYs}
          onClose={() => { setYardSvcOpen(false); setEditingYs(null); }}
          onSaved={() => load()}
        />
      )}

      {correctCnOpen && (
        <CorrectContainerNumberDialog
          oldContainerNumber={container.container_number}
          onClose={() => setCorrectCnOpen(false)}
          onDone={(newCn) => navigate(`/containers/${newCn}`)}
        />
      )}
    </div>
  );
}
